/**
 * BadgeDisplay Component
 *
 * Displays user badges and achievements on the profile page.
 * Shows badge collections, progress toward next badges, and streak info.
 */

import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import api from '../../services/api'
import BadgeCard from './BadgeCard'
import StreakDisplay from './StreakDisplay'
import { createLogger } from '../../utils/logger'

const logger = createLogger('BadgeDisplay')

const BadgeDisplay = ({ userId = null }) => {
  const [badges, setBadges] = useState([])
  const [stats, setStats] = useState(null)
  const [streak, setStreak] = useState(null)
  const [progress, setProgress] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeCategory, setActiveCategory] = useState('all')

  useEffect(() => {
    fetchBadgeData()
  }, [userId])

  const fetchBadgeData = async () => {
    try {
      setLoading(true)
      setError(null)
      // Fetch user badges from API - use userId-specific endpoint if provided
      const endpoint = userId ? `/user/${userId}/badges` : '/badges/me'
      const response = await api.get(endpoint)
      const data = response.data.data
      setBadges(data.badges || [])
      setStats(data.stats)
      setStreak(data.streak)
      setProgress(data.progress || [])
    } catch (err) {
      logger.error('Failed to fetch badges:', err)
      setError('Failed to load achievements. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const categories = [
    { key: 'all', label: 'All' },
    { key: 'MILESTONE', label: 'Milestones' },
    { key: 'MASTERY', label: 'Mastery' },
    { key: 'STREAK', label: 'Streaks' },
    { key: 'ENGAGEMENT', label: 'Engagement' },
    { key: 'IMPROVEMENT', label: 'Improvement' }
  ]

  const filteredBadges =
    activeCategory === 'all' ? badges : badges.filter((b) => b.badge?.category === activeCategory)

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="text-center text-red-500 dark:text-red-400">
          <p>{error}</p>
          <button
            onClick={fetchBadgeData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      {/* Header with Streak */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">Achievements</h2>
          {stats && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {stats.totalEarned} of {stats.totalAvailable} badges earned ({stats.totalPoints}{' '}
              points)
            </p>
          )}
        </div>

        {/* Streak Display */}
        {streak && <StreakDisplay streak={streak} />}
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {categories.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              activeCategory === cat.key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Badges Grid */}
      {filteredBadges.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <svg
            className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          <p>No badges in this category yet</p>
          <p className="text-sm">Keep practicing to earn achievements!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {filteredBadges.map((userBadge) => (
            <BadgeCard key={userBadge.id} badge={userBadge.badge} earnedAt={userBadge.earnedAt} />
          ))}
        </div>
      )}

      {/* Progress to Next Badge */}
      {progress.length > 0 && (
        <div className="mt-8 pt-6 border-t dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Next Up</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {progress.slice(0, 6).map((item) => (
              <div key={item.code} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    {item.name}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {item.current}/{item.target}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress by Category */}
      {stats?.byCategory && (
        <div className="mt-8 pt-6 border-t dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            Progress by Category
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.byCategory.map((cat) => (
              <div key={cat.category} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200 capitalize">
                    {cat.category.toLowerCase().replace('_', ' ')}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {cat.earned}/{cat.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{ width: `${cat.total > 0 ? (cat.earned / cat.total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

BadgeDisplay.propTypes = {
  /** User ID to display badges for (uses authenticated user if not provided) */
  userId: PropTypes.string
}

export default BadgeDisplay
